import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Cloud, 
  LogOut, 
  User, 
  Menu, 
  X, 
  Zap, 
  Clock, 
  Radio,
  Route,
  Calendar
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoImage from '../assets/logo.png';

const Navbar = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: Cloud },
    { path: '/quick-check', label: 'Quick Check', icon: Zap },
    { path: '/optimizer', label: 'Time Optimizer', icon: Clock },
    { path: '/nowcast', label: 'Live View', icon: Radio },
    { path: '/route-risk', label: 'Route Risk', icon: Route },
    { path: '/events', label: 'Events', icon: Calendar },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="glass-card sticky top-4 z-50 mb-6 mx-4 border-white/20">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex flex-col items-center group">
            <motion.div
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.3, type: "spring" }}
              className="relative"
            >
              <img 
                src={logoImage} 
                alt="Atmos Logo" 
                className="h-10 w-auto object-contain"
              />
            </motion.div>
            <p className="text-xs text-white/50 mt-1 whitespace-nowrap">AI-Powered Forecasting</p>
          </Link>

          {/* Desktop Navigation */}
          {currentUser && (
            <div className="hidden md:flex items-center space-x-2 bg-white/5 rounded-2xl p-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 relative group ${
                      isActive(item.path)
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                    {isActive(item.path) && (
                      <motion.div
                        layoutId="navbar-indicator"
                        className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl -z-10"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                  </Link>
                );
              })}
            </div>
          )}

          {/* User Menu */}
          <div className="flex items-center space-x-2">
            {currentUser ? (
              <>
                <Link
                  to="/profile"
                  className="hidden sm:flex items-center glass-button p-2 hover:shadow-lg"
                  title="Profile"
                >
                  <User className="h-4 w-4" />
                </Link>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleLogout}
                  className="flex items-center glass-button p-2 text-red-300 hover:text-red-200 hover:bg-red-500/10 border border-red-400/30 hover:border-red-400/50"
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </motion.button>
              </>
            ) : (
              // Hide Login button on login/register pages
              !location.pathname.includes('/login') && !location.pathname.includes('/register') && (
                <Link to="/login" className="glass-button px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 font-medium">
                  Login
                </Link>
              )
            )}

            {/* Mobile menu button */}
            {currentUser && (
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden glass-button p-3"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </motion.button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {mobileMenuOpen && currentUser && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-white/10 mt-2"
          >
            <div className="px-6 py-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.path}
                    whileHover={{ x: 4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3.5 rounded-xl transition-all ${
                        isActive(item.path)
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  </motion.div>
                );
              })}
              <motion.div whileHover={{ x: 4 }}>
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3.5 rounded-xl text-white/70 hover:bg-white/10 hover:text-white transition-all"
                >
                  <User className="h-5 w-5" />
                  <span className="font-medium">Profile</span>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
